<template>
    <v-sheet tag="nav" position="absolute" width="100%" color="transparent" class="site-top-mobile-menu" :class="cssClasses">
        <div class="bg-secondary site-top-mobile-menu__container">
            <slot name="prepend" />

            <v-expansion-panels v-model="expanded" variant="accordion" class="no-dividers">
                <template v-for="item in topMenu" :key="item.id">
                    <v-expansion-panel v-if="item.childPages?.length" bg-color="secondary" elevation="0" rounded="0">
                        <v-expansion-panel-title
                            class="site-top-mobile-menu__dropdown-title py-0 px-4"
                            ripple
                            color="secondary"
                            hide-actions
                        >
                            <span class="site-top-mobile-menu__dropdown-title-inner">{{ item.displayName }}</span>
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                            <v-row dense>
                                <v-col v-for="child in item.childPages" :key="child.id" cols="6">
                                    <v-btn
                                        variant="text"
                                        :href="child.link"
                                        :target="child.isExternalLink ? '_blank' : undefined"
                                        height="32"
                                        min-width="auto"
                                        class="text-body-1 text-decoration-none pa-0"
                                    >
                                        {{ child.displayName }}
                                        <template v-if="child.isExternalLink" #append>
                                            <v-icon icon="mdi-open-in-new" size="18px"></v-icon>
                                        </template>
                                    </v-btn>
                                </v-col>
                            </v-row>
                        </v-expansion-panel-text>
                    </v-expansion-panel>
                    <v-btn
                        v-else
                        :href="item.link"
                        :target="item.isExternalLink ? '_blank' : undefined"
                        variant="text"
                        ripple
                        class="v-expansion-panel-title site-top-mobile-menu__dropdown-title bg-secondary py-0 px-4 text-decoration-none justify-start pa-0"
                    >
                        {{ item.displayName }}
                        <template v-if="item.isExternalLink" #append>
                            <v-icon icon="mdi-open-in-new" size="20px"></v-icon>
                        </template>
                    </v-btn>
                </template>
            </v-expansion-panels>

            <v-spacer />

            <div class="site-top-mobile-menu__footer-container pa-4 pb-6">
                <div class="site-top-mobile-menu__profile-status">
                    <my-profile-status
                        :menu-items="menuItems"
                        :is-logged-in="isLoggedIn"
                        :profile-info="profileInfo"
                        :current-language="currentLanguage"
                        :hide-my-profile-icon="isMobile"
                        color-theme="inverted"
                        height="32"
                        :readonly="isLoggedIn"
                    />
                </div>
                <div class="site-top-mobile-menu__bottom-menu">
                    <div class="links">
                        <v-btn variant="text" :href="contactUs?.link">
                            <template #prepend>
                                <v-sheet rounded="circle" width="20" height="20" class="d-flex align-center justify-center">
                                    <v-icon icon="mdi-phone" size="16" color="green" />
                                </v-sheet>
                            </template>
                            {{ contactUsPage?.displayName }}
                        </v-btn>
                        <my-profile-link-button
                            :is-logged-in="isLoggedIn"
                            :title="myPageInfo?.displayName"
                            :page-link="myPageLink"
                            link-type="page"
                        />
                    </div>
                    <div class="links">
                        <language-switcher v-model="_currentLanguage" :supported-languages="menuItems.supportedLanguages" height="32" />
                        <my-profile-link-button
                            :is-logged-in="isLoggedIn"
                            :title="profilePageInfo?.displayName"
                            :page-link="profilePageLink"
                            link-type="account"
                        />
                        <logout-link-button
                            :menu-items="menuItems"
                            :is-logged-in="isLoggedIn"
                            :current-language="currentLanguage"
                            height="32"
                        />
                    </div>
                </div>
            </div>
        </div>
    </v-sheet>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { VSheet, VRow, VCol, VBtn } from 'vuetify/components';
import { LanguageSwitcher } from '../LanguageSwitcher';
import { MyProfileStatus } from '../MyProfileStatus';
import { MenuStructure, useSiteNavigation } from '../../composables';
import { useVModel } from '@vueuse/core';
import { useSiteHeader } from '../SiteHeader';
import { ProfileInfo } from '../../models';
import { useProfileNavigation } from '../../composables';
import { LogoutLinkButton, MyProfileLinkButton } from '..';

interface Props {
    menuItems: MenuStructure;
    currentLanguage: string;
    isLoggedIn: boolean;
    modelValue: boolean;
    profileInfo?: ProfileInfo;
}

const props = defineProps<Props>();
const expanded = ref();
const { contactUsPage, topMenu } = useSiteNavigation(props);
const { isMobile } = useSiteHeader();
const contactUs = computed(() => contactUsPage.value?.childPages?.find(x => x.displayName == (isMobile.value ? 'mobile' : 'desktop')));
const _currentLanguage = useVModel(props, 'currentLanguage');
const cssClasses = computed(() => (props.modelValue ? 'site-top-mobile-menu--active' : undefined));
const { profilePageLink, profilePageInfo, myPageInfo, myPageLink } = useProfileNavigation(props);

watch(
    () => props.modelValue,
    value => {
        document.documentElement.classList.toggle('overflow-hidden');

        if (!value) {
            expanded.value = undefined;
        }
    }
);
</script>

<style lang="scss">
@use 'vuetify/settings' as *;
@use 'vuetify/lib/styles/tools/_index' as *;

.no-dividers.v-expansion-panels > .v-expansion-panel::after {
    opacity: 0;
}

.site-top-mobile-menu {
    top: 0;
    height: calc(100dvh - 50px);
    pointer-events: none;
    transform: translateY(-150vh);
    transition-duration: 0.25s;
    transition-property: box-shadow, transform, visibility, width, height, left, right, top, bottom;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);

    &--active {
        transform: translateY(0);
    }

    &__container {
        display: flex;
        height: 100%;
        flex-direction: column;
        pointer-events: all;
    }

    &__footer-container {
        background-color: #20403f;
        margin: 16px;
        overflow: hidden;
    }

    &__profile-status {
        padding-bottom: 6px;
        border-bottom: 1px solid rgba(255, 255, 255, 0.35);

        .v-btn {
            --v-btn-size: #{calc(24px / $font-size-root) * 1rem};
            margin-left: -6px;
        }
    }

    &__bottom-menu {
        display: grid;
        grid-template-columns: 1fr 145px;

        .links {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 4px;
            margin-top: 12px;

            .v-btn {
                --v-btn-size: #{calc(16px / $font-size-root) * 1rem};
            }

            &:first-child {
                margin-left: -10px;
            }
        }
    }

    &__dropdown-title {
        min-height: 62px !important;
        @include typography(24px, 500, 0, 1.4, none);

        &-inner {
            display: inline-block;
            padding-top: 4px;
            padding-bottom: 4px;
            position: relative;

            &:after {
                content: '';
                position: absolute;
                left: 0;
                bottom: 0;
                height: 4px;
                width: 0;
                background-color: var(--v-color-green-darken-2, #3cb454);
                transition: width 0.1s ease;

                .v-expansion-panel-title--active & {
                    width: 100%;
                }
            }
        }

        .v-expansion-panel-title__overlay {
            opacity: 0 !important;
        }
    }

    .v-expansion-panel-text__wrapper {
        padding: 8px 16px 4px 28px;
    }
}
</style>
